import { useEffect, useState } from 'react';
import { Box, Typography, Button, TextField, Stack, Dialog, DialogTitle, DialogContent, DialogActions,
  Paper, Table, TableHead, TableRow, TableCell, TableBody, TablePagination, Chip, TableSortLabel } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import AddIcon from '@mui/icons-material/Add';
import { api } from '../services/api';

export default function TenantsPage() {
  const nav = useNavigate();
  const [data, setData] = useState({ items: [], totalCount: 0 });
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(25);
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState({ field: 'name', direction: 'asc' });
  const [open, setOpen] = useState(false);

  const load = () => api.get('/api/tenants', { params: { search, page: page + 1, pageSize: size } }).then(r => setData(r.data));
  useEffect(() => { load(); }, [page, size]); // eslint-disable-line

  const sortedItems = [...data.items].sort((a, b) => {
    const left = String(a[sort.field] || '').toLowerCase();
    const right = String(b[sort.field] || '').toLowerCase();
    const result = left.localeCompare(right);
    return sort.direction === 'asc' ? result : -result;
  });
  const toggleSort = (field) => setSort(current => ({
    field,
    direction: current.field === field && current.direction === 'asc' ? 'desc' : 'asc',
  }));

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h4">Tenants</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setOpen(true)}>Add Tenant</Button>
      </Stack>
      <Paper sx={{ p: 2, mb: 2 }}>
        <form onSubmit={(e) => { e.preventDefault(); setPage(0); load(); }}>
          <TextField fullWidth placeholder="Search by name, domain or email" value={search} onChange={(e) => setSearch(e.target.value)} size="small" />
        </form>
      </Paper>
      <Paper>
        <Table>
          <TableHead><TableRow>
            <TableCell><TableSortLabel active={sort.field === 'name'} direction={sort.field === 'name' ? sort.direction : 'asc'} onClick={() => toggleSort('name')}>Name</TableSortLabel></TableCell><TableCell>Domain</TableCell><TableCell>Industry</TableCell>
            <TableCell>Plan</TableCell><TableCell>Fee</TableCell><TableCell>Status</TableCell>
          </TableRow></TableHead>
          <TableBody>
            {sortedItems.map(t => (
              <TableRow key={t.id} hover sx={{ cursor: 'pointer' }} onClick={() => nav(`/tenants/${t.id}`)}>
                <TableCell>{t.name}</TableCell>
                <TableCell>{t.domainName || '-'}</TableCell>
                <TableCell>{t.industry || '-'}</TableCell>
                <TableCell>{t.subscriptionPlan || '-'}</TableCell>
                <TableCell>{t.currentMonthlyFee != null ? `${t.currencyCode || ''} ${Number(t.currentMonthlyFee).toFixed(2)}` : '-'}</TableCell>
                <TableCell><Chip size="small" label={t.isActive ? 'Active' : 'Inactive'} color={t.isActive ? 'success' : 'default'} /></TableCell>
              </TableRow>
            ))}
            {data.items.length === 0 && <TableRow><TableCell colSpan={6} align="center">No tenants</TableCell></TableRow>}
          </TableBody>
        </Table>
        <TablePagination component="div" count={data.totalCount} page={page} onPageChange={(_, p) => setPage(p)}
          rowsPerPage={size} onRowsPerPageChange={(e) => { setSize(+e.target.value); setPage(0); }} />
      </Paper>
      <CreateTenantDialog open={open} onClose={() => setOpen(false)} onCreated={() => { setOpen(false); load(); }} />
    </Box>
  );
}

function CreateTenantDialog({ open, onClose, onCreated }) {
  const [f, setF] = useState({ name: '', domainName: '', industry: '', contactEmail: '',
    subscriptionPlan: '', initialMonthlyFee: '', currencyCode: 'ZAR' });
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      await api.post('/api/tenants', {
        ...f,
        initialMonthlyFee: f.initialMonthlyFee ? Number(f.initialMonthlyFee) : null
      });
      onCreated();
    } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>New Tenant</DialogTitle>
      <DialogContent>
        <Stack spacing={2} mt={1}>
          <TextField label="Name" required value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} />
          <TextField label="Domain" required value={f.domainName} onChange={(e) => setF({ ...f, domainName: e.target.value })} />
          <TextField label="Industry" value={f.industry} onChange={(e) => setF({ ...f, industry: e.target.value })} />
          <TextField label="Contact email" required value={f.contactEmail} onChange={(e) => setF({ ...f, contactEmail: e.target.value })} />
          <TextField label="Subscription plan" value={f.subscriptionPlan} onChange={(e) => setF({ ...f, subscriptionPlan: e.target.value })} />
          <Stack direction="row" spacing={2}>
            <TextField label="Initial fee" type="number" value={f.initialMonthlyFee} onChange={(e) => setF({ ...f, initialMonthlyFee: e.target.value })} />
            <TextField label="Currency" value={f.currencyCode} onChange={(e) => setF({ ...f, currencyCode: e.target.value })} />
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={submit} disabled={busy || !f.name}>Create</Button>
      </DialogActions>
    </Dialog>
  );
}
